/**
 * Expected answer field component.
 *
 * Renders the appropriate input field for expected answers based on question type.
 *
 * @module ExpectedAnswerField
 */

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

const normalizeTrueFalse = (value: string | string[]): string | string[] => {
  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase();
    if (lowerValue === 'true') return 'True';
    if (lowerValue === 'false') return 'False';
  }
  return value;
};

export const ExpectedAnswerField = ({
  questionType,
  value,
  onChange,
  options,
  idPrefix = '',
}: {
  questionType?: string;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  options?: string[];
  idPrefix?: string;
}) => {
  if (questionType === 'multiple-choice-single') {
    return (
      <Select
        value={(value as string) || ''}
        onValueChange={(val) => onChange(val)}
      >
        <SelectTrigger className="mt-1">
          <SelectValue placeholder="Select expected answer" />
        </SelectTrigger>
        <SelectContent>
          {(options || [])
            .filter((opt) => opt.trim())
            .map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    );
  }

  if (questionType === 'multiple-choice-multi') {
    return (
      <div className="mt-2 space-y-2">
        {(options || [])
          .filter((opt) => opt.trim())
          .map((option, index) => {
            let currentAnswers: string[];
            if (Array.isArray(value)) {
              currentAnswers = value;
            } else if (value) {
              currentAnswers = [value];
            } else {
              currentAnswers = [];
            }
            const isChecked = currentAnswers.includes(option);

            return (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`expected-${idPrefix}-${index}`}
                  checked={isChecked}
                  onCheckedChange={(checked) => {
                    let newAnswers: string[];
                    if (checked) {
                      newAnswers = [...currentAnswers, option];
                    } else {
                      newAnswers = currentAnswers.filter(
                        (answer) => answer !== option,
                      );
                    }
                    onChange(newAnswers);
                  }}
                />
                <Label
                  htmlFor={`expected-${idPrefix}-${index}`}
                  className="font-ui text-sm"
                >
                  {option}
                </Label>
              </div>
            );
          })}
      </div>
    );
  }

  if (questionType === 'true-false') {
    const normalizedValue = normalizeTrueFalse(value as string) as string;
    return (
      <Select
        value={normalizedValue || ''}
        onValueChange={(val) => onChange(val)}
      >
        <SelectTrigger className="mt-1">
          <SelectValue placeholder="Select expected answer" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="True">True</SelectItem>
          <SelectItem value="False">False</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (questionType === 'text-short') {
    return (
      <Input
        id="expectedAnswer"
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter expected answer"
        className="mt-1"
      />
    );
  }

  if (questionType === 'text-long' || questionType === 'audio-recording' || questionType === 'live-conversation') {
    return (
      <Textarea
        id="expectedAnswer"
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter expected answer"
        className="mt-1"
        rows={4}
      />
    );
  }

  if (questionType === 'fill-in-blank') {
    // For fill-in-blank, expectedAnswer should be an array of 5 answers
    let answers: string[];
    if (Array.isArray(value)) {
      answers = value;
    } else if (typeof value === 'string' && value.trim()) {
      try {
        answers = JSON.parse(value);
        if (!Array.isArray(answers)) {
          answers = [answers];
        }
      } catch {
        // If not valid JSON, treat as single answer
        answers = [value];
      }
    } else {
      answers = ['', '', '', '', ''];
    }

    // Ensure we have exactly 5 answers
    while (answers.length < 5) {
      answers.push('');
    }
    answers = answers.slice(0, 5);

    return (
      <div className="mt-2 space-y-2">
        <Label className="font-ui text-sm font-medium">
          Expected Answers (5 blanks):
        </Label>
        {answers.map((answer, index) => (
          <div key={index} className="flex items-center gap-2">
            <Label htmlFor={`blank-${idPrefix}-${index}`} className="font-ui text-sm w-8 shrink-0">
              {index + 1}:
            </Label>
            <Input
              id={`blank-${idPrefix}-${index}`}
              value={answer}
              onChange={(e) => {
                const newAnswers = [...answers];
                newAnswers[index] = e.target.value;
                onChange(newAnswers);
              }}
              placeholder={`Answer for blank ${index + 1}`}
              className="flex-1"
            />
          </div>
        ))}
        <p className="font-ui text-xs text-muted-foreground mt-2">
          Enter the expected answer for each blank in order. Answers will be stored as a JSON array.
        </p>
      </div>
    );
  }

  return null;
};
